import type { ReactNode } from "react";

import { DocumentViewerDialog } from "@/domains/documents/components/DocumentViewerDialog";
import { useDocumentViewer } from "@/domains/documents/hooks/useDocumentViewer";
import {
  canQuickViewDocumentRow,
  isDwgParentDoc,
  useDrawingViewer,
} from "@/domains/documents/hooks/useDrawingViewer";
import { usePermissions } from "@/hooks/usePermissions";

/** What a dashboard row knows about a document — enough to open it. */
export interface DashboardDocument {
  id: number;
  filename: string;
  file_path: string | null;
  content_type: string | null;
  size: number | null;
  state: string | null;
  created_at: string;
  uploaderName?: string;
}

export interface DashboardDocumentOpener {
  /** Whether the row can be opened in place (previewable, or a DWG the viewer may open). */
  canOpen: (doc: DashboardDocument) => boolean;
  open: (doc: DashboardDocument) => void;
  /** Mount once, anywhere in the widget: the shared viewer dialog. */
  dialog: ReactNode;
}

/**
 * Open a document from a dashboard widget the way the documents list does: a
 * DWG goes to the native Drawings viewer (the document viewer's DWG branch
 * opened the converted child PDF, which no longer exists), everything else to
 * the shared document viewer. One copy of that rule for every dashboard feed —
 * "Uploaded today" and What's new both use it.
 */
export function useDashboardDocumentOpener(buildingId: string): DashboardDocumentOpener {
  const viewer = useDocumentViewer();
  const { canViewDrawingCompareTool } = usePermissions();
  const { openDrawingViewer } = useDrawingViewer([], undefined, {
    enabled: canViewDrawingCompareTool,
    buildingId,
  });

  const canOpen = (doc: DashboardDocument) =>
    (isDwgParentDoc(doc) || doc.file_path != null) &&
    canQuickViewDocumentRow(doc, canViewDrawingCompareTool);

  const open = (doc: DashboardDocument) => {
    if (isDwgParentDoc(doc)) {
      if (canViewDrawingCompareTool) openDrawingViewer([doc.id]);
      return;
    }
    if (doc.file_path == null) return;
    void viewer.openViewer({
      id: doc.id,
      filename: doc.filename,
      file_path: doc.file_path,
      building_id: buildingId,
      content_type: doc.content_type,
      size: doc.size,
      created_at: doc.created_at,
      created_by_name: doc.uploaderName || null,
    });
  };

  const dialog = (
    <DocumentViewerDialog
      document={viewer.document}
      blobUrl={viewer.blobUrl}
      blobData={viewer.blobData}
      open={viewer.isOpen}
      onOpenChange={viewer.setIsOpen}
      originalDwgFilePath={viewer.originalDwgFilePath ?? undefined}
    />
  );

  return { canOpen, open, dialog };
}
